import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { RefreshCw, Check, X, CalendarClock } from "lucide-react";
import api from "@/config/api";
import { MasteryStars, levelLabel } from "@/components/VerseCard";
import { AudioPlayer } from "@/components/AudioPlayer";
import { GamifiedButton } from "@/components/GamifiedButton";
import { Loading, EmptyState } from "@/components/Feedback";
import { useSeo } from "@/hooks/useSeo";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }); } catch { return "—"; }
}

export default function Review() {
  useSeo("Réviser", "Révision intelligente par répétition espacée : révise les versets du Coran au bon moment selon ton niveau de maîtrise pour ancrer ta mémoire.");
  const qc = useQueryClient();
  const [active, setActive] = useState(null); // progress item being reviewed
  const [reveal, setReveal] = useState(false);

  const { data: due, isLoading } = useQuery({
    queryKey: ["reviews-due"],
    queryFn: async () => (await api.get("/reviews/due")).data,
  });

  const submit = useMutation({
    mutationFn: async ({ id, success }) => (await api.post("/reviews/submit", { progress_id: id, success })).data,
    onSuccess: (d, vars) => {
      toast[vars.success ? "success" : "message"](vars.success ? `Bravo ! Niveau ${d.level} 🎉` : "Pas de souci, on révisera encore.");
      setActive(null); setReveal(false);
      qc.invalidateQueries({ queryKey: ["reviews-due"] });
      qc.invalidateQueries({ queryKey: ["progress-summary"] });
    },
    onError: () => toast.error("Erreur lors de la révision."),
  });

  if (isLoading) return <Loading full />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-black text-slate-800">Réviser</h1>
        <p className="text-slate-500">Répétition espacée — révise au bon moment pour ancrer ta mémoire.</p>
      </div>

      <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-100 rounded-3xl px-5 py-4">
        <div className="w-11 h-11 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center">
          <CalendarClock className="w-6 h-6" />
        </div>
        <div>
          <div className="font-extrabold text-slate-800">Versets à réviser aujourd'hui</div>
          <div className="text-sm text-slate-500">{due?.length || 0} verset(s) en attente</div>
        </div>
      </div>

      {!due?.length ? (
        <EmptyState icon={RefreshCw} title="Rien à réviser pour l'instant 🎉" subtitle="Reviens plus tard ou apprends de nouveaux versets dans l'onglet Apprendre." />
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {due.map((item, i) => (
            <motion.div key={item.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              className="bg-white p-5 rounded-3xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)]" data-testid={`review-card-${item.id}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-extrabold text-slate-800">{item.surah_name_fr}</span>
                <span className="text-sm font-bold text-slate-400">verset {item.ayah_number}</span>
              </div>
              <div className="flex items-center justify-between mb-4">
                <MasteryStars level={item.level} />
                <span className="text-xs font-bold text-slate-400">{levelLabel(item.level)}</span>
              </div>
              <div className="flex items-center justify-between text-xs text-slate-400 mb-4">
                <span>Révisé : {fmtDate(item.last_reviewed)}</span>
                <span>Prochaine : {fmtDate(item.next_review)}</span>
              </div>
              <GamifiedButton variant="secondary" className="w-full py-3 text-sm" onClick={() => { setActive(item); setReveal(false); }} data-testid={`review-start-${item.id}`}>
                Réviser maintenant
              </GamifiedButton>
            </motion.div>
          ))}
        </div>
      )}

      {/* Review flow modal */}
      <AnimatePresence>
        {active && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-slate-900/40" onClick={() => setActive(null)}>
            <motion.div initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }} onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-white rounded-3xl p-6 sm:p-8" data-testid="review-modal">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="font-extrabold text-slate-800">{active.surah_name_fr}</div>
                  <div className="text-sm text-slate-400">verset {active.ayah_number}</div>
                </div>
                <button onClick={() => setActive(null)} data-testid="review-close"><X className="w-5 h-5 text-slate-400" /></button>
              </div>

              <p className="text-center text-slate-500 font-semibold mb-4">Récite ce verset de mémoire, puis vérifie.</p>

              {reveal ? (
                <div className="animate-float-up">
                  <p className="font-arabic text-3xl sm:text-4xl leading-[2.3] text-slate-900 text-right mb-4" data-testid="review-arabic">{active.text_ar}</p>
                  <p className="text-slate-600 border-t border-slate-100 pt-4 mb-4">{active.translation_fr}</p>
                  <AudioPlayer src={active.audio_url} className="mb-6" />
                  <p className="text-center font-bold text-slate-500 mb-3">As-tu bien récité ?</p>
                  <div className="grid grid-cols-2 gap-3">
                    <GamifiedButton variant="ghost" className="!text-red-500 !border-red-200 !shadow-[0_4px_0_#fecaca]" onClick={() => submit.mutate({ id: active.id, success: false })} disabled={submit.isPending} data-testid="review-fail">
                      <X className="w-5 h-5" /> À revoir
                    </GamifiedButton>
                    <GamifiedButton variant="secondary" onClick={() => submit.mutate({ id: active.id, success: true })} disabled={submit.isPending} data-testid="review-success">
                      <Check className="w-5 h-5" /> Réussi
                    </GamifiedButton>
                  </div>
                </div>
              ) : (
                <div className="py-8 text-center">
                  <div className="font-arabic text-4xl text-slate-200 mb-6">••• •••</div>
                  <GamifiedButton onClick={() => setReveal(true)} data-testid="review-reveal">Afficher le verset</GamifiedButton>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
